import {
	Address,
	ContactAddressDTO,
	ContactReportedAddressDTO,
} from "@/type/api";

export function formatAddress(address?: Address): string {
	if (!address) {
		return "undefined";
	}
	if (address.number === "" && address.street === "") {
		return "no address provided";
	}
	return `${address.number.trim()} ${address.street.trim()}, ${address.locality}`;
}
export function formatAddressShort(a: Address | undefined): string {
	if (!a) return "unknown";
	return `${a.number} ${a.street}, ${a.locality}`;
}
// formatAddressText renders a site's display address: the structured
// number+street when present, else the raw line — unless that raw line is a
// PO Box (no street components), which is not a usable street address.
export function formatAddressText(addr: {
	number?: string;
	street?: string;
	raw?: string;
}): string {
	const street = [addr.number, addr.street].filter(Boolean).join(" ");
	if (street) return street;
	const raw = addr.raw || "";
	if (raw && !/^po\s*box/i.test(raw.trim())) return raw;
	return "Unknown";
}
// formatContactAddress renders an address a contact reaches through a Site it is
// linked to. This is the one street line for such an address: the contact search
// matches on exactly this text, so a row a search returned shows the words that
// matched it.
export function formatContactAddress(address: ContactAddressDTO): string {
	return [
		address.number,
		address.street,
		address.unit,
		address.locality,
		address.region,
		address.postal_code,
	]
		.filter(Boolean)
		.join(" ");
}
// formatContactReportedAddress renders an address a contact filed a public
// report about. It is the one line both the contact search and the detail card
// use, so a row a search returned shows the words that matched it. A report may
// never have resolved to an address row, in which case there is no component to
// show and the raw text is all the report carries.
export function formatContactReportedAddress(
	address: ContactReportedAddressDTO,
): string {
	return formatContactAddress(address) || address.raw.trim();
}
export function formatBigNumber(n: number): string {
	// Convert the number to a string
	const numStr = n.toString();

	// Add commas every three digits from the right
	let result = "";
	for (let i = 0; i < numStr.length; i++) {
		if (i > 0 && (numStr.length - i) % 3 === 0) {
			result += ",";
		}
		result += numStr[i];
	}

	return result;
}
// formatDate renders a full instant: the calendar day together with the time
// of day and its zone, for a value whose clock time is meaningful.
export function formatDate(date: Date): string {
	return new Intl.DateTimeFormat(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZoneName: "short",
	}).format(date);
}

// formatDateOnly renders just the calendar day ("Dec 31, 2026"), no time of
// day. A bare-day value — a revisit pin, a stored date with no meaningful
// clock time — would otherwise show the placeholder time it was generated at.
export function formatDateOnly(date: Date): string {
	return new Intl.DateTimeFormat(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	}).format(date);
}

// formatDateOnlyLong spells the same day out in full ("Thursday, December 31,
// 2026") for a tooltip that adds the weekday without inventing a clock time.
export function formatDateOnlyLong(date: Date): string {
	return new Intl.DateTimeFormat(undefined, {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
	}).format(date);
}

export function formatDistance(meters: number | undefined) {
	if (meters === undefined || meters === null) {
		return "unknown";
	}
	if (meters < 1) {
		const mm = Math.round(meters * 1000);
		return `${mm} mm`;
	} else if (meters >= 1000) {
		const km = Math.round(meters / 1000);
		return `${km} km`;
	} else {
		const m = Math.round(meters);
		return `${m} m`;
	}
}
// usPhoneDigits returns the ten national digits of a US number, whatever shape
// the stored value takes. comms.phone.e164 reached us in four shapes -- "+1...",
// "1..." with eleven digits, ten bare digits, and the punctuated "(323)
// 356-6326" -- because the column was written before the phone row became a
// shared record. Migration 00545 rewrote those rows and added a constraint that
// refuses anything but an E.164 string, so reading the shape rather than the
// prefix is now defence in depth; a value that is not unambiguously a US number
// still returns null and is shown exactly as stored, because an international
// number and a truncated fragment must not be reshaped into a guess.
export function usPhoneDigits(value: string): string | null {
	const digits = value.replace(/\D/g, "");
	if (digits.length === 11 && digits.startsWith("1")) {
		return digits.substring(1);
	}
	return digits.length === 10 ? digits : null;
}

// formatPhoneUS renders a US number the way the operator writes it:
// "+1 (323) 356-6326".
export function formatPhoneUS(value: string): string {
	const national = usPhoneDigits(value);
	if (national === null) {
		return value;
	}
	return `+1 (${national.substring(0, 3)}) ${national.substring(3, 6)}-${national.substring(6)}`;
}

export function formatRelativeTime(dateString: string): string {
	if (!dateString) return "";

	const date = new Date(dateString);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / 60000);
	const diffHours = Math.floor(diffMins / 60);
	const diffDays = Math.floor(diffHours / 24);

	if (diffMins < 1) return "just now";
	if (diffMins < 60) return `${diffMins} min ago`;
	if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
	return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
}
export function formatReportID(id: string): string {
	if (id.length === 12) {
		return `${id.substring(0, 4)}-${id.substring(4, 8)}-${id.substring(8)}`;
	}
	return id;
}
// durationSeconds is the elapsed time between two UTC ISO timestamps, or null
// when the end is unknown (an open treatment has no end_datetime).
export function durationSeconds(
	start: string,
	end: string | null,
): number | null {
	if (!end) {
		return null;
	}
	return (new Date(end).getTime() - new Date(start).getTime()) / 1000;
}
// formatDuration renders a duration in seconds compactly ("2h 5m", "45m",
// "30s"), or "—" when the duration is unknown.
export function formatDuration(seconds: number | null): string {
	if (seconds === null) {
		return "—";
	}
	const total = Math.max(0, Math.round(seconds));
	const hours = Math.floor(total / 3600);
	const minutes = Math.floor((total % 3600) / 60);
	const secs = total % 60;
	if (hours > 0) {
		return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
	}
	if (minutes > 0) {
		return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
	}
	return `${secs}s`;
}
export function formatTimeRelative(t: Date): string {
	const now = new Date();
	const diffMs = now.getTime() - t.getTime();

	const hours = diffMs / (1000 * 60 * 60);

	if (hours > 0) {
		if (hours < 1) {
			const minutes = diffMs / (1000 * 60);
			return `${Math.floor(minutes)} minutes ago`;
		} else if (hours < 24) {
			return `${Math.floor(hours)} hours ago`;
		} else {
			const days = hours / 24;
			return `${Math.floor(days)} days ago`;
		}
	} else {
		if (hours < -24) {
			const days = hours / 24;
			return `in ${Math.floor(-1 * days)} days`;
		} else if (hours < -1) {
			return `in ${Math.floor(-1 * hours)} hours`;
		} else {
			const minutes = diffMs / (1000 * 60);
			if (minutes > -1) {
				const seconds = diffMs / 1000;
				return `in ${Math.floor(-1 * seconds)} seconds`;
			}
			return `in ${Math.floor(-1 * minutes)} minutes`;
		}
	}
}
